class DDProductCardSlider {
  constructor(card) {
    this.card = card;
    this.slides = Array.from(card.querySelectorAll('[data-dd-product-card-slide]'));
    this.prevButton = card.querySelector('[data-dd-product-card-prev]');
    this.nextButton = card.querySelector('[data-dd-product-card-next]');
    this.index = 0;
    this.count = this.slides.length;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragOffset = 0;
    this.hasDragged = false;
    this.suppressClick = false;
    this.activePointerId = null;
    this.hasPointerCapture = false;
    this.isTouching = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchOffset = 0;
    this.isHorizontalTouch = false;

    if (this.count <= 1) {
      this.update();
      return;
    }

    this.card.classList.add('is-draggable');

    this.prevButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.goTo(this.index - 1);
    });

    this.nextButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.goTo(this.index + 1);
    });

    this.card.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.card.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.card.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.card.addEventListener('pointercancel', this.onPointerUp.bind(this));
    this.card.addEventListener('lostpointercapture', this.onPointerUp.bind(this));
    this.card.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    this.card.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    this.card.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
    this.card.addEventListener('touchcancel', this.onTouchCancel.bind(this), { passive: true });
    this.card.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    this.slides.forEach((slide) => {
      slide.setAttribute('draggable', 'false');

      slide.querySelectorAll('img').forEach((image) => {
        image.setAttribute('draggable', 'false');
      });

      slide.addEventListener('click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      });
    });

    this.update();
  }

  goToMediaId(mediaId) {
    if (!mediaId) return false;

    const targetIndex = this.slides.findIndex((slide) => slide.dataset.ddCardMediaId === String(mediaId));
    if (targetIndex < 0) return false;

    this.goTo(targetIndex);
    return true;
  }

  onPointerDown(event) {
    if (event.pointerType === 'touch') return;
    if (event.button !== undefined && event.button !== 0) return;
    if (event.target.closest('button')) return;

    this.isDragging = true;
    this.hasDragged = false;
    this.suppressClick = false;
    this.dragStartX = event.clientX;
    this.dragOffset = 0;
    this.activePointerId = event.pointerId;
    this.hasPointerCapture = false;
    this.card.classList.add('is-dragging');
  }

  onPointerMove(event) {
    if (event.pointerType === 'touch') return;
    if (!this.isDragging) return;

    this.dragOffset = event.clientX - this.dragStartX;

    if (Math.abs(this.dragOffset) > 4) {
      this.hasDragged = true;

      if (!this.hasPointerCapture && this.activePointerId !== null) {
        this.card.setPointerCapture?.(this.activePointerId);
        this.hasPointerCapture = true;
      }
    }

    const maxOffset = this.card.clientWidth * 0.75;
    const limitedOffset = Math.max(Math.min(this.dragOffset, maxOffset), -maxOffset);
    this.card.style.setProperty('--dd-product-card-drag-offset', `${limitedOffset}px`);
  }

  onPointerUp(event) {
    if (event.pointerType === 'touch') return;
    if (!this.isDragging) return;

    const threshold = Math.max(45, this.card.clientWidth * 0.18);
    const shouldMoveNext = this.dragOffset < -threshold;
    const shouldMovePrev = this.dragOffset > threshold;

    this.isDragging = false;
    this.suppressClick = this.hasDragged;
    this.card.classList.remove('is-dragging');
    if (this.hasPointerCapture && this.activePointerId !== null) {
      this.card.releasePointerCapture?.(this.activePointerId);
    }
    this.activePointerId = null;
    this.hasPointerCapture = false;
    this.card.style.setProperty('--dd-product-card-drag-offset', '0px');

    if (shouldMoveNext) {
      this.goTo(this.index + 1);
    } else if (shouldMovePrev) {
      this.goTo(this.index - 1);
    } else {
      this.update();
    }

    window.setTimeout(() => {
      this.hasDragged = false;
      this.suppressClick = false;
    }, 160);
  }

  onTouchStart(event) {
    if (!event.touches || event.touches.length !== 1) return;
    if (event.target.closest('button')) return;

    this.isTouching = true;
    this.hasDragged = false;
    this.suppressClick = false;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchOffset = 0;
    this.isHorizontalTouch = false;
  }

  onTouchMove(event) {
    if (!this.isTouching || !event.touches || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (!this.isHorizontalTouch && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.isHorizontalTouch = true;
      this.hasDragged = true;
      this.card.classList.add('is-dragging');
    }

    if (!this.isHorizontalTouch) return;

    event.preventDefault();
    this.touchOffset = deltaX;

    const maxOffset = this.card.clientWidth * 0.75;
    const limitedOffset = Math.max(Math.min(this.touchOffset, maxOffset), -maxOffset);
    this.card.style.setProperty('--dd-product-card-drag-offset', `${limitedOffset}px`);
  }

  onTouchEnd(event) {
    if (!this.isTouching) return;

    const touch = event.changedTouches && event.changedTouches[0];
    const deltaX = touch ? touch.clientX - this.touchStartX : this.touchOffset;
    const deltaY = touch ? touch.clientY - this.touchStartY : 0;
    const threshold = Math.max(32, this.card.clientWidth * 0.14);
    const shouldMoveNext = this.isHorizontalTouch && Math.abs(deltaX) > threshold && Math.abs(deltaX) > Math.abs(deltaY) && deltaX < 0;
    const shouldMovePrev = this.isHorizontalTouch && Math.abs(deltaX) > threshold && Math.abs(deltaX) > Math.abs(deltaY) && deltaX > 0;

    this.isTouching = false;
    this.suppressClick = this.hasDragged || this.isHorizontalTouch;
    this.card.classList.remove('is-dragging');
    this.card.style.setProperty('--dd-product-card-drag-offset', '0px');

    if (shouldMoveNext) {
      this.goTo(this.index + 1);
    } else if (shouldMovePrev) {
      this.goTo(this.index - 1);
    } else {
      this.update();
    }

    window.setTimeout(() => {
      this.hasDragged = false;
      this.suppressClick = false;
      this.isHorizontalTouch = false;
    }, 180);
  }

  onTouchCancel() {
    if (!this.isTouching) return;

    this.isTouching = false;
    this.isHorizontalTouch = false;
    this.hasDragged = false;
    this.card.classList.remove('is-dragging');
    this.card.style.setProperty('--dd-product-card-drag-offset', '0px');
    this.update();
  }
  goTo(index) {
    if (index < 0) {
      this.index = this.count - 1;
    } else if (index >= this.count) {
      this.index = 0;
    } else {
      this.index = index;
    }

    this.update();
  }

  update() {
    this.card.style.setProperty('--dd-product-card-active-index', this.index);
    this.card.style.setProperty('--dd-product-card-drag-offset', '0px');
    this.card.style.setProperty('--dd-product-card-media-count', Math.max(this.count, 1));
  }
}

function initDDProductCardSliders(root = document) {
  root.querySelectorAll('[data-dd-product-card-slider]').forEach((card) => {
    if (card.dataset.ddProductCardSliderInitialized === 'true') return;
    card.dataset.ddProductCardSliderInitialized = 'true';
    card.ddProductCardSlider = new DDProductCardSlider(card);
  });
}

function setDDCardLinks(card, url) {
  if (!url) return;

  card.querySelectorAll('[data-dd-card-link]').forEach((link) => {
    link.setAttribute('href', url);
  });

  const wishlist = card.querySelector('[data-dd-card-wishlist]');
  if (wishlist) {
    wishlist.dataset.productUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
  }
}

function updateDDCardImage(card, swatch) {
  const media = card.querySelector('[data-dd-product-card-slider]');
  if (!media) return;

  const mediaId = swatch.dataset.variantMediaId;
  const variantImage = swatch.dataset.variantImage;
  const movedToExistingSlide = media.ddProductCardSlider?.goToMediaId(mediaId);

  if (movedToExistingSlide || !variantImage) return;

  const firstImage = media.querySelector('[data-dd-product-card-slide] img');
  if (!firstImage) return;

  firstImage.src = variantImage;
  firstImage.removeAttribute('srcset');
  media.ddProductCardSlider?.goTo(0);
}

function updateDDCardQuickAdd(card, swatch) {
  const variantIdInput = card.querySelector('[data-dd-card-variant-id-input]');
  const quickAddButton = card.querySelector('[data-dd-card-quick-add-button]');
  const wishlist = card.querySelector('[data-dd-card-wishlist]');
  const variantId = swatch.dataset.variantId;
  const isAvailable = swatch.dataset.variantAvailable === 'true';

  if (variantIdInput && variantId) {
    variantIdInput.value = variantId;
  }

  if (wishlist && variantId) {
    wishlist.dataset.variantId = variantId;
  }

  if (!quickAddButton) return;

  quickAddButton.textContent = isAvailable ? 'Add to Bag' : 'Sold Out';
  quickAddButton.disabled = !isAvailable;
  quickAddButton.classList.toggle('dd-collection-card__quick-add-button--soldout', !isAvailable);
}

function updateDDCardSwatch(card, swatch) {
  card.querySelectorAll('[data-dd-card-swatch]').forEach((button) => {
    const isActive = button === swatch;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });

  const optionLabel = card.querySelector('[data-dd-card-option-label]');
  if (optionLabel && swatch.dataset.variantOption) {
    optionLabel.textContent = swatch.dataset.variantOption;
  }

  const price = card.querySelector('[data-dd-card-price]');
  if (price && swatch.dataset.variantPrice) {
    price.textContent = swatch.dataset.variantPrice;
  }

  setDDCardLinks(card, swatch.dataset.variantUrl);
  updateDDCardQuickAdd(card, swatch);
  updateDDCardImage(card, swatch);
}

function initDDProductCardSwatches(root = document) {
  root.querySelectorAll('[data-dd-collection-card]').forEach((card) => {
    if (card.dataset.ddCardSwatchesInitialized === 'true') return;

    card.dataset.ddCardSwatchesInitialized = 'true';

    card.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-dd-card-swatch]');
      if (!swatch || !card.contains(swatch)) return;

      event.preventDefault();
      event.stopPropagation();
      updateDDCardSwatch(card, swatch);
    });
  });
}

function getDDCartElement() {
  return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
}

function getDDCartSectionIds(cart) {
  if (cart && typeof cart.getSectionsToRender === 'function') {
    return cart.getSectionsToRender().map((section) => section.id);
  }

  if (cart?.tagName?.toLowerCase() === 'cart-drawer') return ['cart-drawer', 'cart-icon-bubble'];
  return ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
}

async function fetchDDCartWithSections(cart) {
  const sectionIds = getDDCartSectionIds(cart);
  const sectionsUrl = new URL(window.routes?.cart_url || '/cart', window.location.origin);
  sectionsUrl.searchParams.set('sections', sectionIds.join(','));
  sectionsUrl.searchParams.set('sections_url', window.location.pathname);

  const sections = await fetch(sectionsUrl.toString(), {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  }).then((response) => response.json());

  const cartState = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  }).then((response) => response.json());

  return {
    ...cartState,
    sections,
  };
}

async function renderDDCartContents(response) {
  const cart = getDDCartElement();
  if (!cart) return false;

  let state = response;
  if (!state.sections) {
    state = await fetchDDCartWithSections(cart);
  }

  try {
    if (typeof cart.renderContents === 'function') {
      cart.renderContents(state);
      return true;
    }

    if (typeof cart.open === 'function') {
      cart.open();
      return true;
    }
  } catch (error) {
    const fallbackState = await fetchDDCartWithSections(cart);
    if (typeof cart.renderContents === 'function') {
      cart.renderContents(fallbackState);
      return true;
    }
    console.warn('[dd-product-card] Cart drawer render failed.', error);
  }

  return false;
}

function publishDDCartUpdate(response, variantId) {
  if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'dd-card-quick-add',
    productVariantId: variantId,
    cartData: response,
  }).catch((error) => {
    console.warn('[dd-product-card] Cart subscribers failed after drawer render.', error);
  });
}

async function handleDDCardQuickAddSubmit(event) {
  const form = event.currentTarget;
  const button = form.querySelector('[data-dd-card-quick-add-button]') || form.querySelector('[type="submit"]');
  const cart = getDDCartElement();

  event.preventDefault();
  if (button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;

  const originalText = button ? button.textContent : '';
  const formData = new FormData(form);
  const variantId = formData.get('id');

  if (!variantId) {
    form.submit();
    return;
  }

  if (cart) {
    formData.append('sections', getDDCartSectionIds(cart));
    formData.append('sections_url', window.location.pathname);
    cart.setActiveElement?.(button || document.activeElement);
  }

  if (button) {
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    button.textContent = 'Adding...';
  }

  try {
    const response = await fetch(window.routes?.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    }).then((response) => response.json());

    if (response.status) {
      throw new Error(response.description || response.message || 'Unable to add this item.');
    }

    if (cart) {
      await renderDDCartContents(response);
      publishDDCartUpdate(response, variantId);
    } else {
      window.location = window.routes?.cart_url || '/cart';
    }
  } catch (error) {
    console.error(error);
    if (button) button.textContent = error.message || 'Add failed';
    window.setTimeout(() => {
      if (button) button.textContent = originalText;
    }, 1800);
  } finally {
    if (button) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
      if (!button.disabled && button.textContent === 'Adding...') button.textContent = originalText;
    }
  }
}

function initDDCardQuickAddForms(root = document) {
  root.querySelectorAll('[data-dd-card-quick-add-form]').forEach((form) => {
    if (form.dataset.ddCardQuickAddInitialized === 'true') return;
    form.dataset.ddCardQuickAddInitialized = 'true';
    form.addEventListener('submit', handleDDCardQuickAddSubmit);
  });
}
window.initDDProductCardSliders = initDDProductCardSliders;
window.initDDProductCardSwatches = initDDProductCardSwatches;
window.initDDCardQuickAddForms = initDDCardQuickAddForms;

initDDProductCardSliders();
initDDProductCardSwatches();
initDDCardQuickAddForms();

document.addEventListener('shopify:section:load', (event) => {
  initDDProductCardSliders(event.target);
  initDDProductCardSwatches(event.target);
  initDDCardQuickAddForms(event.target);
});

if ('MutationObserver' in window) {
  const ddProductCardSliderObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches('[data-dd-product-card-slider]')) {
          initDDProductCardSliders(node.parentElement || document);
          initDDProductCardSwatches(node.parentElement || document);
          initDDCardQuickAddForms(node.parentElement || document);
          return;
        }

        if (node.querySelector('[data-dd-product-card-slider], [data-dd-collection-card]')) {
          initDDProductCardSliders(node);
          initDDProductCardSwatches(node);
          initDDCardQuickAddForms(node);
        }
      });
    });
  });

  ddProductCardSliderObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}
